/**
 * Momentum: the meter a fighter fills by being worth watching.
 *
 * It rises from landing blows, and much faster from catching someone in the
 * middle of their own — a counter. It rises from taunting, which is the point
 * of taunting: the fighter is not hitting anybody, they are selling the fight,
 * and the meter is the crowd. It falls, slowly, when nothing is happening,
 * because a fighter circling out of range for ten seconds is not building
 * anything.
 *
 * How fast it rises is the fighter's own **charisma**. Two fighters throwing the
 * same punches do not earn the same meter, which is the whole reason the stat
 * exists rather than a constant.
 *
 * And `drive`, which is everything *outside* the fighter that is currently
 * making the meter move faster or slower. Today that is exactly one thing —
 * heat — but this module does not import it and does not name it, because
 * "how loud is the room" is not momentum's business to know the shape of. It
 * is a function rather than a number so it is read at the moment of the gain:
 * a counter thrown in a hot fight pays what the fight is worth *now*.
 *
 * Full, it can be spent to go **blazin** for a fixed time. Right now that is a
 * flag and a colour, and the flag is what step 19 reads when it decides whether
 * a blow is allowed to finish a fight.
 *
 * Pure — no three.js, no timers of its own. Feed it `dt`.
 */

import MOMENTUM from './momentum.json';

export { MOMENTUM };

/** Things that put momentum on the meter. Anything else is not a source. */
export const SOURCES = Object.freeze(['hit', 'counter', 'blocked', 'taunt']);

export function createMomentum({ charisma = 1, drive = () => 1, ...overrides } = {}) {
  const config = Object.freeze({ ...MOMENTUM, ...overrides });
  if (!(charisma > 0)) throw new RangeError(`charisma must be positive, got ${charisma}`);
  // Who the fighter is, plus what they are wearing tonight (step 93): gear
  // raises it and a crowd bored of the same look takes the gear's share back
  // (`bank.charisma()`). Set between fights, never during one.
  let current = charisma;

  let value = 0;
  let sinceGain = Infinity;
  let blazinLeft = 0;

  /** Never allowed to be zero or negative: a drive that stops the meter dead
   *  would be a rule, and this is a multiplier. */
  function driveNow() {
    const value = drive();
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  return {
    config,
    get charisma() {
      return current;
    },
    /** A new charisma, for the next gain on. Positive, like the one it replaces. */
    setCharisma(next) {
      if (!(next > 0)) throw new RangeError(`charisma must be positive, got ${next}`);
      current = next;
      return current;
    },

    /** What the room is currently worth. For the overlay, and for tests. */
    get drive() {
      return driveNow();
    },

    get value() {
      return value;
    },
    get max() {
      return config.max;
    },
    /** For drawing. */
    get fraction() {
      return value / config.max;
    },
    get full() {
      return value >= config.max;
    },
    get decaying() {
      return !blazinLeft && sinceGain >= config.decayDelay && value > 0;
    },

    get blazin() {
      return blazinLeft > 0;
    },
    get blazinLeft() {
      return Math.max(0, blazinLeft);
    },
    /** Whether `ignite()` would work right now. */
    get canIgnite() {
      return value >= config.max && blazinLeft <= 0;
    },

    /**
     * Adds the meter a source is worth, scaled by charisma.
     *
     * Gains are ignored while blazin: the meter has already been spent, and
     * letting it refill mid-blaze would let one full bar run the rest of the
     * round.
     */
    gain(source, scale = 1) {
      const base = config.gain[source];
      if (!(base > 0) || blazinLeft > 0 || !(scale > 0)) return 0;
      const before = value;
      // Three multipliers, and they are three different questions. Charisma is
      // the fighter, the scale is their style, and the drive is the room. A
      // loud fighter in a showy style in a hot fight really should fill it
      // several times faster than a quiet one grinding out a dull round.
      value = Math.min(config.max, value + base * current * scale * driveNow());
      sinceGain = 0;
      return value - before;
    },

    /** Raw addition, for tests and for anything that is not a named source. */
    add(amount) {
      if (!(amount > 0) || blazinLeft > 0) return 0;
      const before = value;
      value = Math.min(config.max, value + amount);
      sinceGain = 0;
      return value - before;
    },

    /**
     * Spends a full meter to go blazin. Refuses anything less than full, and
     * refuses to stack — an extension would make the meter a subscription.
     */
    ignite() {
      if (value < config.max || blazinLeft > 0) return false;
      value = 0;
      blazinLeft = config.blazin.duration;
      sinceGain = 0;
      return true;
    },

    /** Ends a blaze early. The meter is already spent; it does not come back. */
    extinguish() {
      blazinLeft = 0;
    },

    update(dt) {
      if (!(dt > 0)) return;

      if (blazinLeft > 0) {
        blazinLeft -= dt;
        if (blazinLeft < 0) blazinLeft = 0;
        // A blaze is its own clock. Nothing decays underneath it.
        return;
      }

      sinceGain += dt;
      if (sinceGain >= config.decayDelay && value > 0) {
        value = Math.max(0, value - config.decayPerSecond * dt);
      }
    },

    reset() {
      value = 0;
      sinceGain = Infinity;
      blazinLeft = 0;
    },
  };
}
